import { css } from "@emotion/react"
import Modal from "./Modal"
import { useRef, useMemo, useState, useLayoutEffect, useEffect } from "react"
import fullLogo from '../../assets/images/logo_large.svg'

export default function PresentationModal({ onClickClose, children, speed, backgroundImage, ...props }) {
    const scrollRef = useRef(null)
    const firstCopyRef = useRef(null)

    const [isOverflowing, setIsOverflowing] = useState(false)
    const [oneHeight, setOneHeight] = useState(0)

    const animationIntervalMs = 120 / (speed ?? 2)

    // Measure overflow + base content height
    useLayoutEffect(() => {
        const el = scrollRef.current
        const first = firstCopyRef.current
        if (!el || !first) return

        const measure = () => {
            // base height of ONE copy
            const h = first.scrollHeight
            // we want to when to return to top
            setOneHeight(h)
            // Is it overflowing with one copy?
            setIsOverflowing(h > el.clientHeight)
        }

        measure()
        const ro = new ResizeObserver(measure)
        ro.observe(el)

        return () => ro.disconnect()
    }, [children])

    // Wrap scroll position for "infinite" illusion when duplicated
    useEffect(() => {
        const el = scrollRef.current
        if (!el) return
        if (!isOverflowing || oneHeight <= 0) return

        let raf = 0

        const interval = setInterval(() => {
            raf = requestAnimationFrame(() => {
                const loopPoint = oneHeight
                if (el.scrollTop >= loopPoint) {
                    el.scrollTop = 0
                } else if (el.scrollTop < 0) {
                    el.scrollTop = 0
                } else {
                    el.scrollTo(0, el.scrollTop + 1)
                }
            })
        }, animationIntervalMs)

        return () => {
            cancelAnimationFrame(raf)
            clearInterval(interval)
        }
    }, [isOverflowing, oneHeight])

    // Render either single or duplicated content.
    // Note: duplicate is aria-hidden so screen readers don't read twice.
    const content = useMemo(() => {

        if (!isOverflowing) return <div ref={firstCopyRef} key='0'>{children}</div>

        return <>
            <div ref={firstCopyRef} key='0'>{children}</div>
            <div aria-hidden="true" key='1'>{children}</div>
            <div aria-hidden="true" key='2'>{children}</div>
        </>
    }, [children, isOverflowing])

    // close on any key press, and exit fullscreen
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.repeat) return // ignore held-down key spam
            if (['Shift', 'Alt', 'Control', 'Meta'].includes(e.key)) return
            onClickClose()
        }

        const handleFullscreenChange = () => {
            // If fullscreen was exited by the browser (Esc, etc)
            if (!document.fullscreenElement) onClickClose()
        }

        document.addEventListener('keydown', handleKeyDown)
        document.addEventListener('fullscreenchange', handleFullscreenChange)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.removeEventListener('fullscreenchange', handleFullscreenChange)

            if (document.fullscreenElement) {
                document.exitFullscreen().catch(() => {
                    console.error("Failed to exit fullscreen")
                })
            }
        }
    }, [])

    return (
        <Modal
            padOverlay={false}
            padContent={false}
            hideCloseButon={true}
            onClickClose={onClickClose}
            onKeyDown={() => onClickClose()}
            onClick={(e) => {
                e.stopPropagation()
                onClickClose()
            }}
            css={css`
                width: 100vw;
                height: 100vh;
                position: relative;
            `}
            {...props}
        >

            <div
                ref={scrollRef}
                css={css`
                    height: 100vh;
                    width: 100%;
                    overflow-y: auto;
                    overscroll-behavior: contain;
                    -webkit-overflow-scrolling: touch;

                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;

                    > div {
                        width: 100%;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                    }

                    ::-webkit-scrollbar {
                        width: 0;
                        height: 0;
                    }

                    /* Firefox */
                    scrollbar-width: none;

                    /* IE 10+ (legacy, but harmless) */
                    -ms-overflow-style: none;
                `}
            >
                {content}
            </div>

            <div css={css`
                position: absolute;
                bottom: 0em;
                right: 0em;
                opacity: 0.20;
                padding: 0.5em;
                border-radius: 0.5em;
                background-color: rgba(255,255,255,0.1);
                z-index: 1000;
            `}>
                <img src={fullLogo} alt="" css={css`width: 8em;`} />
            </div>
        </Modal>
    );
}